import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest} from 'rxjs';

import { map, debounceTime, distinctUntilChanged } from 'rxjs/operators';

import {SelectableVehicle, VehicleOperation, EquipmentStatus, VehicleValidation} from '../models/vehicle-validation';

import { VehicleService } from 'src/app/vehicles/services/vehicle.service';

import { FleetChecklistApiService } from './fleet-checklist-api.service';


@Injectable({
  providedIn: 'root'
})
export class VehicleValidationManagerService {

  public vehiclesListSubject$ = new BehaviorSubject<SelectableVehicle[]>([]);
  public operationsSubject$ = new BehaviorSubject<VehicleOperation[]>([]);

  // public operationsSubject$ = new BehaviorSubject<VehicleOperation[]>([]);

  private searchFilterSubject = new BehaviorSubject<string>('');
  private filterByOperationSubject = new BehaviorSubject<any>(null);
  private filterByGroupSubject = new BehaviorSubject<any>(null);
  private filterByConvoySubject = new BehaviorSubject<any>(null);
  private filteredVehiclesSubject = new BehaviorSubject<SelectableVehicle[]>([]);


  private loadingSubject = new BehaviorSubject<boolean>(false);
  private errorMessageSubject = new BehaviorSubject<string | null>(null);

  public filteredVehicles$ = this.filteredVehiclesSubject.asObservable();
  public searchFilter$ = this.searchFilterSubject.asObservable();
  public filterByOperation$ = this.filterByOperationSubject.asObservable();
  public filterByGroup$ = this.filterByGroupSubject.asObservable();
  public filterByConvoy$ = this.filterByConvoySubject.asObservable();

  public loading$: Observable<boolean> = this.loadingSubject.asObservable();
  public errorMessage$: Observable<string | null> = this.errorMessageSubject.asObservable();

  private debouncedSearch$: Observable<string> = this.searchFilterSubject.asObservable().pipe(
    debounceTime(300),         // Espera 300ms de silencio antes de emitir
    distinctUntilChanged()     // Solo emite si el texto realmente cambió respecto al anterior
  );
  public computedFilteredVehicles$: Observable<SelectableVehicle[]> = combineLatest([
    this.vehiclesListSubject$,
    this.debouncedSearch$,
    this.filterByOperation$,
    this.filterByGroup$,
    this.filterByConvoy$
  ]).pipe(
    map(([fleets, search, operation, group, convoy]) => this.applyFilter(fleets, search, operation, group, convoy))
  );

  constructor(
    private vehicleService: VehicleService,
    private api : FleetChecklistApiService
  ) {
    this.computedFilteredVehicles$.subscribe(v => {
      console.log('Filtered vehicles:', v);
      this.filteredVehiclesSubject.next(v);
    });
    this.vehicleService.dataVehicleCompleted.subscribe(async() => {
      let vehiclesFixes = await this.vehicleService.getVehiclesFixes();
      const vehiclesPlate = this.vehicleService.getVehiclesUnitsByPlate();
      const operationsMap = new Map();
      let vehiclesList = [];

      for (const [plate, vehicles] of vehiclesPlate) {
        const vehicleUnit = vehiclesFixes.data.find((v: any) => v.var_placa == plate);
        if(!vehicleUnit){
          continue;
        }

        const imeis = vehicles.map(v => v.IMEI);

        const gps = imeis.find((imei: string) => !imei.startsWith('E'))?.toString();
        const cipia = imeis.find((imei: string) => imei.startsWith('E'))?.toString();

        const vehicle : SelectableVehicle = {
          id: vehicleUnit.id,
          plate,
          unitCode: vehicleUnit.var_codigo_interno,
          operations: [...new Set(vehicles.map(v => v.idoperation))],
          groups: [...new Set(vehicles.map(v => v.idgrupo))],
          convoys: [...new Set(vehicles.map(v => v.idconvoy))],
          gps,
          cipia
        };

        vehiclesList.push(vehicle);

        vehicles.forEach(v => {
          // OPERATION
          if (!operationsMap.has(v.idoperation)) {
            operationsMap.set(v.idoperation, {
              id: v.idoperation,
              name: v.nameoperation,
              groups: []
            });
          }

          const operation = operationsMap.get(v.idoperation);

          // GROUP
          let group = operation.groups.find((g: any) => g.id === v.idgrupo);
          if (!group) {
            group = {
              id: v.idgrupo,
              name: v.namegrupo,
              convoys: []
            };
            operation.groups.push(group);
          }

          // CONVOY
          let convoyExists = group.convoys.find((c: any) => c.id === v.idconvoy);
          if (!convoyExists) {
            group.convoys.push({
              id: v.idconvoy,
              name: v.nameconvoy
            });
          }
        });
      }

      let operationsFiltered = Array.from(operationsMap.values());

      this.vehiclesListSubject$.next(vehiclesList);
      this.operationsSubject$.next(operationsFiltered);
    });
    this.vehicleService.initialize();
  }


  applyFilter(vehicles: SelectableVehicle[], search: string, operation: any, group: any, convoy: any): SelectableVehicle[] {
    return vehicles.filter((vehicle) => {
      // Filter by text search
      if (search) {
        const query = search.toLowerCase();
        const matchPlate = vehicle.plate && vehicle.plate.toLowerCase().includes(query);
        const matchCode = vehicle.unitCode && vehicle.unitCode.toLowerCase().includes(query);
        if (!matchPlate && !matchCode) return false;
      }
      // Filter by Operation
      if (operation) {
        if (!vehicle.operations.includes(operation.id)) return false;
      }
      // Filter by Group
      if (group) {
        if (!vehicle.groups.includes(group.id)) return false;
      }
      // Filter by Convoy
      if (convoy) {
        if (!vehicle.convoys.includes(convoy.id)) return false;
      }
      return true;
    });
  }

  findVehicleByPlate(plate: string): SelectableVehicle | null {
    let vehiclesList = this.vehiclesListSubject$.getValue();
    return vehiclesList.find((v: any) => v.plate == plate || v.plate == plate.replace('-', '')) || null;
  }

  // Getter/setter for Search Query
  getSearchFilter(): string {
    return this.searchFilterSubject.getValue();
  }
  setSearchFilter(query: string): void {
    this.searchFilterSubject.next(query);
  }

  getVehiclesByIds(ids: number[]): SelectableVehicle[] {
    const vehicles = this.vehiclesListSubject$.getValue();

    return vehicles.filter(v => ids.includes(v.id));
  }

  // Getter/setter for Operation
  getSelectedOperation(): any {
    return this.filterByOperationSubject.getValue();
  }
  setOperation(operation: any): void {
    this.filterByOperationSubject.next(operation);
    if(this.filterByGroupSubject.getValue()){
      this.filterByGroupSubject.next(null);
    }
    if(this.filterByConvoySubject.getValue()){
      this.filterByConvoySubject.next(null);
    }
  }

  // Getter/setter for Group
  getSelectedGroup(): any {
    return this.filterByGroupSubject.getValue();
  }
  setGroup(group: any): void {
    this.filterByGroupSubject.next(group);
    if(this.filterByConvoySubject.getValue()){
      this.filterByConvoySubject.next(null);
    }
  }

  // Getter/setter for Convoy
  getSelectedConvoy(): any {
    return this.filterByConvoySubject.getValue();
  }
  setConvoy(convoy: any): void {
    this.filterByConvoySubject.next(convoy);
  }

  // Getters for options
  getOperations(): VehicleOperation[] {
    return this.operationsSubject$.getValue();
  }
  getFilteredGroups(): any[] {
    const operation = this.filterByOperationSubject.getValue();
    return operation ? operation.groups || [] : [];
  }
  getFilteredConvoys(): any[] {
    const group = this.filterByGroupSubject.getValue();
    return group ? group.convoys || [] : [];
  }

  // Synchronous access to filtered vehicles list
  getFilteredVehicles(): SelectableVehicle[] {
    return this.filteredVehiclesSubject.getValue();
  }

  clearFilters(): void {
    this.searchFilterSubject.next('');
    this.filterByOperationSubject.next(null);
    this.filterByGroupSubject.next(null);
    this.filterByConvoySubject.next(null);
  }

  async getEquipmentStatus(vehiclesValidation : VehicleValidation[]) : Promise<EquipmentStatus[] | null>{

    let vehicles = this.getVehiclesByIds(
      vehiclesValidation.map(v => Number(v.id)) || []
    )
      .filter(v => v.gps != null && v.gps !== '')
      .map(v => {
        return {
          IMEI: v.gps,
          name: `(${v.unitCode}) ${v.plate}`,
        };
      });

    const from = new Date();
    const to = new Date(from);
    to.setDate(from.getDate() - 5);
    let equipmentStatus = await this.api.getLastEventOperative(vehicles,from, to)

    return equipmentStatus
  }
}
